import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useProfile } from 'context/profile';
import { ClientOptions } from 'models/Client';
import { Paginated } from 'models/Paginated';
import { forResourceClients } from 'services/api/client';
import { updateProfile } from 'services/api/profile';
import { useToastActions } from 'stores/toastStore';

type AuthorizedCheckOptions = {
  resourceType?: string;
  onClientSwitched?: () => void;
  fallbackPath?: string;
  notFoundText?: string;
};

export function useActingClientAuthorizedCheck(
  resourceId?: string,
  {
    resourceType = 'episode',
    onClientSwitched,
    fallbackPath = '/patients',
    notFoundText = 'Patient not found',
  }: AuthorizedCheckOptions = {}
) {
  const { profile } = useProfile();
  const { addToast } = useToastActions();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { mutateAsync } = useMutation({
    mutationFn: updateProfile,
  });
  const autoAuthorize = !profile.canChangeClientScope;
  const [authorizationChecked, setAuthorizationChecked] = useState(autoAuthorize);

  // Reset authorization check when the resource changes
  useEffect(() => {
    setAuthorizationChecked(autoAuthorize);
  }, [resourceId, autoAuthorize]);

  const {
    data: queryResponse,
    isError: resourceLookupFailed,
    isFetching: resourceLookupInFlight,
  } = useQuery({
    queryKey: ['clientsForResource', { resourceType, resourceId }],
    queryFn: ({ signal }) => forResourceClients({ id: resourceId as string, type: resourceType }, signal),
    enabled: !authorizationChecked && !!resourceId,
  });

  // A failed lookup (bad/unknown id) is a not-found, not a permanent skeleton. Wait out
  // any in-flight refetch first — a cached error from a transient failure would otherwise
  // eject the user on remount before the retry gets a chance to resolve.
  useEffect(() => {
    if (!resourceLookupFailed || resourceLookupInFlight) return;

    addToast({ text: notFoundText });
    navigate(fallbackPath);
  }, [resourceLookupFailed, resourceLookupInFlight, addToast, navigate, notFoundText, fallbackPath]);

  const checkActingClientAuthorized = useCallback(
    async (queryResponse: Paginated<ClientOptions>) => {
      try {
        const clientIds = queryResponse?.data.map((client) => client.id) ?? [];

        if (!clientIds.includes(profile.actingClient.id)) {
          const newActingClientId = profile.isAdmin
            ? clientIds[0]
            : clientIds.find((id) => profile.client?.leafDescendantIds.includes(id));

          if (!newActingClientId) {
            addToast({ text: notFoundText });
            navigate(fallbackPath);
            return;
          }

          // mutateAsync so the profile is updated before "authorizationChecked" flips.
          const response = await mutateAsync({ actingClientId: newActingClientId });

          queryClient.setQueryData(['profile'], () => response);
          onClientSwitched?.();
          setAuthorizationChecked(true);
        }

        setAuthorizationChecked(true);
      } catch (e) {
        // A failed switch must not strand the caller behind an unauthorized gate —
        // exit to the fallback the same way an inaccessible resource does.
        addToast({ text: e.message });
        navigate(fallbackPath);
      }
    },
    [addToast, mutateAsync, navigate, queryClient, profile, onClientSwitched, fallbackPath, notFoundText]
  );

  useEffect(() => {
    if (queryResponse) {
      checkActingClientAuthorized(queryResponse);
    }
  }, [queryResponse, checkActingClientAuthorized]);

  return authorizationChecked;
}
